import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, Form, FormikProvider, FieldArray } from "formik";
import * as Yup from "yup";
import {
  createSubscription,
  updateSubscription,
  getSubscriptionsList,
} from "../../appRedux/actions/subscriptions";
import { getPlatformsList } from "../../appRedux/actions/platforms";
import { hideDialog as hideDialogCommon } from "../../appRedux/actions/common";
import config from "../../utils/config";

const STATUSES = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

const BILLING_CYCLES = [
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "YEARLY", label: "Yearly" },
  { value: "CUSTOM", label: "Custom days" },
];

const INITIAL_TIER = {
  name: "",
  price: "",
  currency: "INR",
  billingCycle: "MONTHLY",
  customCycleDays: "",
  features: "",
  isActive: true,
};

const INITIAL = {
  title: "",
  description: "",
  merchant: "",
  status: "DRAFT",
  priority: 100,
  tiers: [INITIAL_TIER],
};

function FormView({ isUpdate }) {
  const dispatch = useDispatch();
  const subscriptionInfo = useSelector((state) => state.subscriptions.subscriptionInfo);
  const platformList = useSelector((state) => state.platforms.platformList || []);
  const [initialValues, setInitialValues] = useState(INITIAL);

  useEffect(() => {
    dispatch(getPlatformsList({ page: 1, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isUpdate && subscriptionInfo && subscriptionInfo._id) {
      setInitialValues({
        title: subscriptionInfo.title || "",
        description: subscriptionInfo.description || "",
        merchant: subscriptionInfo.merchant || "",
        status: subscriptionInfo.status || "DRAFT",
        priority: subscriptionInfo.priority ?? 100,
        tiers: subscriptionInfo.tiers && subscriptionInfo.tiers.length > 0
          ? subscriptionInfo.tiers.map(t => ({
              name: t.name || "",
              price: t.price ?? "",
              currency: t.currency || "INR",
              billingCycle: t.billingCycle || "MONTHLY",
              customCycleDays: t.customCycleDays ?? "",
              features: Array.isArray(t.features) ? t.features.join(", ") : (t.features || ""),
              isActive: t.isActive !== false,
            }))
          : [INITIAL_TIER],
      });
    } else {
      setInitialValues(INITIAL);
    }
  }, [isUpdate, subscriptionInfo]);

  const schema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    merchant: Yup.string().required("Merchant is required"),
    priority: Yup.number().min(0).optional(),
    tiers: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required("Tier name is required"),
        price: Yup.number().min(0, "Price must be ≥ 0").required("Price is required"),
        currency: Yup.string().required(),
        billingCycle: Yup.string().oneOf(["MONTHLY", "QUARTERLY", "YEARLY", "CUSTOM"]).required(),
        customCycleDays: Yup.number().when("billingCycle", {
          is: "CUSTOM",
          then: (n) => n.min(1, "Must be ≥ 1").required("Required for Custom cycle"),
        }),
        features: Yup.string().optional(),
      })
    ).min(1, "At least one tier is required"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: schema,
    onSubmit: (values) => {
      const payload = {
        title: values.title,
        description: values.description,
        merchant: values.merchant,
        priority: Number(values.priority) || 100,
        status: values.status,
        tiers: values.tiers.map((t) => ({
          name: t.name,
          price: Number(t.price),
          currency: t.currency,
          billingCycle: t.billingCycle,
          customCycleDays: t.billingCycle === "CUSTOM" ? Number(t.customCycleDays) : undefined,
          features: t.features ? t.features.split(",").map(f => f.trim()).filter(Boolean) : [],
          isActive: t.isActive,
        })),
      };

      if (isUpdate && subscriptionInfo?._id) {
        dispatch(updateSubscription(subscriptionInfo._id, payload))
          .then(() => {
            dispatch(getSubscriptionsList()).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      } else {
        dispatch(createSubscription(payload))
          .then(() => {
            dispatch(getSubscriptionsList()).catch(() => {});
            dispatch(hideDialogCommon());
          })
          .catch(() => {});
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
  } = formik;

  const merchants = platformList || [];

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={handleSubmit} className="signin-form px-2">
        <div className="row g-3">
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>
                Subscription Title <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                type="text"
                placeholder="e.g. Premium Learning Pass"
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("title")}
              />
              {touched.title && errors.title && (
                <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "12px", fontWeight: 500 }}>{errors.title}</p>
              )}
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>
                Merchant <span className="text-danger">*</span>
              </label>
              <select 
                className="form-control form-select" 
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("merchant")}
              >
                <option value="">Select merchant</option>
                {merchants.map((m) => (
                  <option key={m._id || m.code} value={m.code}>
                    {m.name}
                  </option>
                ))}
              </select>
              {touched.merchant && errors.merchant && (
                <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "12px", fontWeight: 500 }}>{errors.merchant}</p>
              )}
            </div>
          </div>
          <div className="col-md-12">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Description</label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Brief description of what this subscription offers..."
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("description")}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Status</label>
              <select 
                className="form-control form-select" 
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("status")}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500, fontSize: "14px", color: "#374151" }}>Priority</label>
              <input
                className="form-control"
                type="number"
                min={0}
                style={{ borderRadius: "6px", border: "1px solid #d1d5db", padding: "10px 12px" }}
                {...getFieldProps("priority")}
              />
            </div>
          </div>

          {/* Pricing Tiers Section */}
          <div className="col-md-12 mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3" style={{ borderBottom: "2px solid #f3f4f6", paddingBottom: "12px" }}>
              <h5 className="m-0" style={{ fontWeight: 600, color: "#111827", fontSize: "16px" }}>
                Pricing Tiers
              </h5>
            </div>
            <FieldArray name="tiers">
              {({ push, remove }) => (
                <div>
                  {values.tiers.map((tier, index) => (
                    <div
                      key={index}
                      className="card border-0 mb-3 shadow-sm"
                      style={{
                        backgroundColor: "#f9fafb",
                        border: "1px solid #e5e7eb",
                        borderRadius: "10px",
                        padding: "16px",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <span style={{ fontWeight: 600, color: "#4b5563", fontSize: "14px" }}>
                          <i className="fa fa-tag text-primary mr-1" /> Tier #{index + 1}
                        </span>
                        {values.tiers.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm text-danger border-0 p-0 hover:text-red-700 d-flex align-items-center"
                            style={{ fontWeight: 500, fontSize: "13px", gap: "4px", backgroundColor: "transparent" }}
                            onClick={() => remove(index)}
                          >
                            <i className="fa fa-trash" /> Remove Tier
                          </button>
                        )}
                      </div>
                      <div className="row g-2">
                        <div className="col-md-4">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Tier Name <span className="text-danger">*</span></label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="e.g. Learner Pro"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`tiers.${index}.name`)}
                            />
                            {touched.tiers?.[index]?.name && errors.tiers?.[index]?.name && (
                              <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.tiers[index].name}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Price <span className="text-danger">*</span></label>
                            <input
                              className="form-control form-control-sm"
                              type="number"
                              min={0}
                              placeholder="0.00"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`tiers.${index}.price`)}
                            />
                            {touched.tiers?.[index]?.price && errors.tiers?.[index]?.price && (
                              <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.tiers[index].price}</p>
                            )}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Currency</label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="INR"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`tiers.${index}.currency`)}
                            />
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Billing Cycle</label>
                            <select
                              className="form-control form-control-sm form-select"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`tiers.${index}.billingCycle`)}
                            >
                              {BILLING_CYCLES.map((bc) => (
                                <option key={bc.value} value={bc.value}>
                                  {bc.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {tier.billingCycle === "CUSTOM" && (
                          <div className="col-md-4">
                            <div className="form-group mb-2">
                              <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Cycle Days <span className="text-danger">*</span></label>
                              <input
                                className="form-control form-control-sm"
                                type="number"
                                min={1}
                                style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                                {...getFieldProps(`tiers.${index}.customCycleDays`)}
                              />
                              {touched.tiers?.[index]?.customCycleDays && errors.tiers?.[index]?.customCycleDays && (
                                <p className="error-msg text-danger style-sm mt-1 mb-0" style={{ fontSize: "11px", fontWeight: 500 }}>{errors.tiers[index].customCycleDays}</p>
                              )}
                            </div>
                          </div>
                        )}
                        <div className="col-md-4">
                          <div className="form-group mb-2">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563", display: "block" }}>Active Status</label>
                            <div className="form-check form-switch mt-1 pt-1">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                style={{ cursor: "pointer" }}
                                checked={tier.isActive}
                                {...getFieldProps(`tiers.${index}.isActive`)}
                              />
                              <label className="form-check-label" style={{ fontSize: "12px", color: "#6b7280", marginLeft: "6px" }}>
                                {tier.isActive ? "Active" : "Inactive"}
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-1">
                            <label className="col-form-label-sm pb-1" style={{ fontWeight: 500, color: "#4b5563" }}>Features (comma separated list)</label>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="e.g. Offline access, Certificate, Mentorship support"
                              style={{ borderRadius: "4px", border: "1px solid #d1d5db" }}
                              {...getFieldProps(`tiers.${index}.features`)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mb-3 d-flex align-items-center"
                    style={{ borderRadius: "6px", gap: "6px", padding: "6px 12px", fontWeight: 500 }}
                    onClick={() => push(INITIAL_TIER)}
                  >
                    <i className="fa fa-plus" /> Add New Tier
                  </button>
                </div>
              )}
            </FieldArray>
          </div>
        </div>
        <div className="d-flex justify-content-end gap-2 mt-4 pt-3" style={{ borderTop: "1px solid #e5e7eb" }}>
          <button 
            type="submit" 
            className="btn btn-primary px-4 py-2" 
            style={{ borderRadius: "6px", fontWeight: 500, minWidth: "120px" }}
          >
            {isUpdate && subscriptionInfo?._id ? "Save Changes" : "Create Subscription"}
          </button>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default FormView;
